import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Store, Loader2, PlusCircle, QrCode, Keyboard, X, Sparkles, Clock3 } from 'lucide-react';
import { useProgramContext } from '../../context/ProgramContext';
import AddVpaDrawer from './AddVpaDrawer';
import SelectContextFirst from '../../components/SelectContextFirst';
import type { Tenant } from '../../types/domain';

const LAST_USED_TENANT_KEY = 'vpa_last_tenant_id';

function CollectScreen() {
  const navigate = useNavigate();
  const { tenants, loading, selectedProgramId, isWalletAddressCollection } = useProgramContext();
  const [search, setSearch] = useState('');
  const [selectedShop, setSelectedShop] = useState<Tenant | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!search.trim()) return tenants;
    const q = search.toLowerCase().trim();
    return tenants.filter(
      (t) =>
        (t.tenantName || '').toLowerCase().includes(q) ||
        (t.tenantBrand || '').toLowerCase().includes(q) ||
        ((t as any).storeCoordinator || '').toLowerCase().includes(q) ||
        ((t as any).phone || '').toString().includes(q),
    );
  }, [tenants, search]);

  const lastUsedTenant = useMemo(() => {
    const stored = localStorage.getItem(LAST_USED_TENANT_KEY);
    if (!stored) return null;
    const parsed = Number(stored);
    if (Number.isNaN(parsed)) return null;
    return tenants.find((t) => t.tenantId === parsed) ?? null;
  }, [tenants]);

  const markAsLastUsed = (tenantId: number) => {
    localStorage.setItem(LAST_USED_TENANT_KEY, String(tenantId));
  };

  const handleSelectShop = (shop: Tenant) => {
    markAsLastUsed(shop.tenantId);
    setSelectedShop(shop);
    setDrawerOpen(true);
  };

  const handleSuccess = () => {
    setSelectedShop(null);
    setDrawerOpen(false);
  };

  return (
    <div className="min-h-full bg-surface">
      <header className="fc-header">
        <h1 className="flex items-center gap-2 text-sm sm:text-base font-semibold text-slate-900">
          <PlusCircle className="w-5 h-5 text-brand" />
          {isWalletAddressCollection ? 'Collect wallet address' : 'Collect VPA'}
        </h1>
        <p className="text-xs sm:text-[13px] text-slate-500 mt-0.5">
          {isWalletAddressCollection
            ? 'Select a shop to add a wallet address'
            : 'Select a shop to add VPA'}
        </p>
      </header>

      <div className="p-3 pb-5 space-y-4">
        {selectedProgramId == null ? (
          <SelectContextFirst />
        ) : (
          <>
            <section className="fc-card p-3.5">
              <h2 className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 mb-2">
                Fastest flow
              </h2>
              <p className="text-sm text-slate-700">
                Tap a shop and choose <span className="font-semibold">Scan QR</span> to collect{' '}
                {isWalletAddressCollection ? 'a wallet address' : 'a VPA'} in a few seconds.
              </p>
            </section>
            {lastUsedTenant && !search && (
              <section className="fc-card p-3.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 flex items-center gap-1.5">
                      <Clock3 className="w-3.5 h-3.5" />
                      Last used shop
                    </p>
                    <p className="text-sm font-semibold text-slate-900 truncate mt-1">
                      {lastUsedTenant.tenantName}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{lastUsedTenant.tenantBrand}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      markAsLastUsed(lastUsedTenant.tenantId);
                      navigate(`/tenant/vpa/add/${lastUsedTenant.tenantId}`, { state: { returnTo: 'app' } });
                    }}
                    className="fc-btn-primary min-h-[40px] px-3 text-xs"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Quick Scan
                  </button>
                </div>
              </section>
            )}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search shop..."
                autoFocus
                className="fc-input pl-11"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500 px-1">
              {filtered.length} shop{filtered.length !== 1 ? 's' : ''} available
            </p>

            {loading && tenants.length === 0 ? (
              <div className="fc-card-muted p-8 text-center text-gray-500 flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-brand" />
                <span>Loading shops...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-200 rounded-lg p-8 text-center text-slate-500 flex flex-col items-center gap-3 transition-colors">
                <Store className="w-10 h-10 text-slate-300" />
                <span>{search ? 'No shops match your search' : 'No shops in this program'}</span>
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map((shop) => (
                  <div
                    key={shop.tenantId}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelectShop(shop)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectShop(shop);
                      }
                    }}
                    className="fc-list-item"
                  >
                    <div className="flex items-center gap-3">
                      {shop.storeLogo && (
                        <img
                          src={shop.storeLogo}
                          alt=""
                          className="w-10 h-10 object-contain rounded border border-slate-100 bg-white"
                        />
                      )}
                      <div>
                        <p className="font-medium text-slate-900">{shop.tenantName}</p>
                        <p className="text-sm text-slate-500">{shop.tenantBrand}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsLastUsed(shop.tenantId);
                          navigate(`/tenant/vpa/add/${shop.tenantId}`, { state: { returnTo: 'app' } });
                        }}
                        className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-[11px] font-semibold text-brand bg-brand/10 hover:bg-brand/15"
                        aria-label={`Scan QR for ${shop.tenantName ?? 'shop'}`}
                      >
                        <QrCode className="w-3.5 h-3.5" />
                        Scan
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectShop(shop);
                        }}
                        className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-[11px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200"
                        aria-label={`Manual add for ${shop.tenantName ?? 'shop'}`}
                      >
                        <Keyboard className="w-3.5 h-3.5" />
                        Type
                      </button>
                      <span className="text-xs text-slate-500 ml-1" title={isWalletAddressCollection ? 'Wallets' : 'VPAs'}>
                        {shop.vpaCount ?? 0}
                      </span>
                      <Store className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {selectedShop && (
        <AddVpaDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          tenantId={selectedShop.tenantId}
          tenantName={selectedShop.tenantName}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}

export default CollectScreen;
